import * as cookie from "cookie";
import { redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { Result } from "better-result";
import { z } from "zod/v4";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { loginSchema } from "#/modules/generic.login/schema.login";
import { toResult } from "#/modules/util.auth/api-errors";
import { getPasskeySession } from "#/modules/util.auth/cookie.passkey.server";
import { client } from "#/modules/util.fetch-client/fetch-client.server";
import { buildLocaleHref } from "#/modules/util.helpers/route-href";
import {
  getLeerlingGestuurdeLes,
  updateBeweegresultaatLeerlinggestuurd,
  updateSociaalEmotioneelResultaatLeerlinggestuurd,
} from "#/modules/les.leerling-gestuurd/api.les.leerling.server";
import {
  getDeviceActivities,
  lockLesson,
  setDeviceActivities,
  unlockLesson,
  unlockLessonWithPasskey,
  verifyDocentPassword,
  verifyPasskeyIdentity,
} from "#/modules/les.leerling-gestuurd/lock-les.server";
import { isUserAdmin } from "#/modules/admin/index.server";
import { isUserImpersonating } from "#/modules/util.impersonate/utils.impersonate.server";
import { translateApiError } from "#/modules/util.errors/translate";
import { apiFailure } from "#/modules/util.errors/api-failure.server";
import { isExpectedApiError } from "#/modules/util.errors/expected";
import { toastErrorMiddleware } from "#/modules/util.errors/middleware.toast-errors";
import type {
  PasskeyRequestOptions,
  PasskeyRequestOptionsResult,
} from "#/modules/util.auth/passkey.functions";
import type { components } from "#/schema";

/** Consumed via a deliberate `window.location.href` hard reload, hence a plain URL string. */
function teacherScoresHref(params: {
  locale: string;
  schoolyear?: string;
  school: string;
  lesson: string;
}) {
  return buildLocaleHref(`/${params.school}/lesson/${params.lesson}/teacher/scores`, params);
}

/**
 * Set the student-led lock on the session (POST so the Set-Cookie actually persists).
 *
 * Portal locked from the /student layout GET loader (RR7 commits loader cookies). This app only
 * persists cookies from POST server fns, so the teacher's "leerling-gestuurd" entry button calls
 * this immediately before navigating into the student view — the exact moment portal locked
 * (`lockLesson()` in the student layout loader). It is the inverse of the unlock POST fns below.
 */
export const lockStudentLesson = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator((data: { lesson: string; school: string }) => data)
  .handler(async ({ data }) => {
    const les = await getLeerlingGestuurdeLes(data.lesson);
    if (Result.isError(les) && (les.error.status === 400 || les.error.status === 404)) {
      return { success: false as const };
    }
    // Record WHICH les locked the session — the guard's redirect target. Deriving the target from
    // the backend's `activeLes` metadata instead was racy: right after the wizard starts a les,
    // activeLes is momentarily null (eventually consistent), and the guard silently no-oped
    // (dex q7hqg0da).
    await lockLesson({ lesId: data.lesson, schoolSlug: data.school });
    return { success: true as const };
  });

/**
 * Layout loader (GET): fetches the student-led lesson data, narrowed to what this device shows.
 * NOTE: locking is intentionally NOT done here. `lockLesson()` writes `lockedLes` to the auth
 * session, but Set-Cookie from a GET server fn does not persist in this app, so calling it here was
 * a no-op. The lock is now established by the `lockStudentLesson` POST above, fired from the
 * teacher's "leerling-gestuurd" entry button.
 */
export const getStudentLessonLayout = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: { lesson: string }) => data)
  .handler(async ({ data }) => {
    const result = await getLeerlingGestuurdeLes(data.lesson);
    if (Result.isError(result)) throw result.error;
    const full = result.value;

    const selection = await getDeviceActivities(data.lesson);
    const lesson = selection ? applyDeviceActivities(full, selection) : full;

    const longestName = lesson?.lesgroepen.reduce(
      (max, lesgroep) =>
        Math.max(
          max,
          lesgroep.leerlingen.reduce(
            (innerMax, leerling) => Math.max(innerMax, leerling.name.length),
            0,
          ),
        ),
      0,
    );

    return {
      lesson,
      longestName,
      deviceActivities: {
        beweegactiviteiten: full.beweegactiviteiten.map((a) => ({ id: a.id, name: a.name })),
        sociaalEmotioneelThemas: full.sociaalEmotioneelThemas.map((t) => ({
          id: t.id,
          name: t.name,
        })),
        selection: {
          beweegactiviteitIds: lesson.beweegactiviteiten.flatMap((a) => (a.id ? [a.id] : [])),
          sociaalEmotioneelThemaIds: lesson.sociaalEmotioneelThemas.flatMap((t) =>
            t.id ? [t.id] : [],
          ),
        },
      },
    };
  });

type LeerlingGestuurdeLes = components["schemas"]["GetLesLeerlinggestuurdResponse"];

/** Ids the les no longer has are ignored; an empty result falls back to everything. */
function applyDeviceActivities(
  les: LeerlingGestuurdeLes,
  selection: { beweegactiviteitIds: string[]; sociaalEmotioneelThemaIds: string[] },
): LeerlingGestuurdeLes {
  const activityIds = new Set(selection.beweegactiviteitIds);
  const themaIds = new Set(selection.sociaalEmotioneelThemaIds);
  const beweegactiviteiten = les.beweegactiviteiten.filter((a) => !!a.id && activityIds.has(a.id));
  const sociaalEmotioneelThemas = les.sociaalEmotioneelThemas.filter(
    (t) => !!t.id && themaIds.has(t.id),
  );
  if (beweegactiviteiten.length + sociaalEmotioneelThemas.length === 0) return les;
  return { ...les, beweegactiviteiten, sociaalEmotioneelThemas };
}

/** Store a beweegresultaat for a student (POST). */
export const submitStudentActivityResult = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(
    (data: {
      lesson: string;
      student: string;
      id: string;
      niveau?: string | number;
      waarde?: string | number;
      beweegdoelIds?: string[];
    }) => data,
  )
  .handler(async ({ data }) => {
    const result = await updateBeweegresultaatLeerlinggestuurd({
      lesId: data.lesson,
      leerlingId: data.student,
      beweegactiviteitId: data.id,
      niveau: data.niveau !== undefined ? Number(data.niveau) : undefined,
      waarde: data.waarde !== undefined ? Number(data.waarde) : undefined,
      beweegdoelIds: data.beweegdoelIds,
    });
    if (Result.isError(result)) return apiFailure(result.error);
    return { success: true as const };
  });

/** Store a sociaal-emotioneel resultaat for a student (POST). */
export const submitStudentSocialResult = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(
    (data: { lesson: string; student: string; id: string; selectedOptions: string[] }) => data,
  )
  .handler(async ({ data }) => {
    const result = await updateSociaalEmotioneelResultaatLeerlinggestuurd({
      lesId: data.lesson,
      leerlingId: data.student,
      sociaalEmotioneelThemaId: data.id,
      sociaalEmotioneelDoelIds: data.selectedOptions,
    });
    if (Result.isError(result)) return apiFailure(result.error);
    return { success: true as const };
  });

/**
 * Unlock the lesson with the teacher's password (POST).
 * On success, throws a redirect to the teacher scores view (wrap with `useServerFn` on the client).
 */
export const unlockStudentLesson = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(
    (data: {
      locale: string;
      schoolyear?: string;
      school: string;
      lesson: string;
      email?: string;
      password?: string;
    }) => data,
  )
  .handler(async ({ data }) => {
    // While impersonating (or browsing as admin) there is no password to submit — `unlockLesson`
    // clears the lock on the impersonation claim / Admin role alone, so validating an empty
    // credential pair would block those paths.
    const credentialless = (await isUserImpersonating()) || (await isUserAdmin());
    const submission = credentialless
      ? { success: true as const }
      : loginSchema.safeParse({
          email: data.email ?? "",
          password: data.password ?? "",
        });

    if (!submission.success) {
      const passwordErrors = submission.error.issues
        .filter((issue) => issue.path[0] === "password")
        .map((issue) => issue.message);
      return {
        success: false as const,
        fieldErrors: { password: passwordErrors },
      };
    }

    const result = await unlockLesson({
      email: data.email ?? "",
      password: data.password ?? "",
    });

    if (Result.isOk(result) && result.value) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/$school/lesson/$lesson/teacher/scores",
        params: {
          locale: data.locale,
          schoolyear: data.schoolyear,
          school: data.school,
          lesson: data.lesson,
        },
      });
    }

    if (Result.isError(result)) {
      if (!isExpectedApiError(result.error)) throw result.error;
      return {
        success: false as const,
        fieldErrors: { password: [translateApiError(result.error)] },
      };
    }

    return {
      success: false as const,
      fieldErrors: { password: [m["les.leerling.password_incorrect"]()] },
    };
  });

/**
 * Passkey-unlock ceremony — step 1 (POST).
 * Fetches WebAuthn request options and stashes the Microsoft Identity cookie in the short-lived
 * passkey session (POST so the Set-Cookie persists). Mirrors util.auth/passkey.functions login step.
 */
export const requestUnlockLessonPasskeyOptions = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .handler(async (): Promise<PasskeyRequestOptionsResult> => {
    // Keep the raw response: the handler reads its Set-Cookie headers below.
    const raw = await client.GET("/auth/login/passkey/request-options", { parseAs: "text" });
    const result = toResult(raw);

    if (Result.isError(result)) {
      return { formError: translateApiError(result.error) };
    }
    const requestOptions = result.value;

    // `getSetCookie()` (not `get("set-cookie")` + `parseCookie`, which mis-handles multiple
    // Set-Cookie headers) returns each Set-Cookie header separately; parse each to find the one
    // we need.
    const cookieValue = raw.response.headers
      .getSetCookie()
      .map((header) => cookie.parseSetCookie(header))
      .find((parsed) => parsed.name === "Identity.TwoFactorUserId")?.value;
    if (cookieValue) {
      const passkeySession = await getPasskeySession();
      await passkeySession.update({ cookie: cookieValue });
    }

    // The schema types this 200 body as `content?: never`; it is the WebAuthn request options JSON.
    const options: PasskeyRequestOptions = JSON.parse(requestOptions);
    return { options };
  });

/**
 * Passkey-unlock ceremony — step 2 (POST).
 * Verifies the WebAuthn credential and, on success, unlocks the lesson and returns the
 * teacher-scores redirect URL for client-side navigation.
 */
export const verifyUnlockLessonPasskey = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(
    (data: {
      locale: string;
      schoolyear?: string;
      school: string;
      lesson: string;
      credentials: string;
    }) => data,
  )
  .handler(async ({ data }) => {
    const result = await unlockLessonWithPasskey({
      credentials: data.credentials,
    });

    if (Result.isOk(result) && result.value) {
      return {
        redirectUrl: teacherScoresHref({
          locale: data.locale,
          schoolyear: data.schoolyear,
          school: data.school,
          lesson: data.lesson,
        }),
      };
    }

    if (Result.isError(result)) {
      return { formError: translateApiError(result.error) };
    }

    return { formError: "Failed to unlock lesson" };
  });

const deviceActivitiesInputSchema = z.object({
  lesson: z.string().min(1),
  beweegactiviteitIds: z.array(z.string()),
  sociaalEmotioneelThemaIds: z.array(z.string()),
  confirm: z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("password"), email: z.string(), password: z.string() }),
    z.object({ kind: z.literal("passkey"), credentials: z.string().min(1) }),
  ]),
});

/** Confirms the docent (password or passkey) without clearing the lock, then writes the session. */
export const saveStudentLessonDeviceActivities = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(deviceActivitiesInputSchema)
  .handler(async ({ data }) => {
    const credentialless = (await isUserImpersonating()) || (await isUserAdmin());
    if (!credentialless) {
      if (data.confirm.kind === "password") {
        const submission = loginSchema.safeParse(data.confirm);
        if (!submission.success) {
          return {
            success: false as const,
            fieldErrors: {
              password: submission.error.issues
                .filter((issue) => issue.path[0] === "password")
                .map((issue) => issue.message),
            },
          };
        }
        const verified = await verifyDocentPassword(submission.data);
        if (Result.isError(verified)) {
          if (!isExpectedApiError(verified.error)) throw verified.error;
          return { success: false as const, formError: translateApiError(verified.error) };
        }
        if (!verified.value) {
          return {
            success: false as const,
            fieldErrors: { password: [m["les.leerling.password_incorrect"]()] },
          };
        }
      } else {
        const verified = await verifyPasskeyIdentity({ credentials: data.confirm.credentials });
        if (Result.isError(verified)) {
          return { success: false as const, formError: translateApiError(verified.error) };
        }
        if (!verified.value) {
          return {
            success: false as const,
            formError: m["les.leerling.unlock_passkey_failed"](),
          };
        }
      }
    }

    const les = await getLeerlingGestuurdeLes(data.lesson);
    if (Result.isError(les)) throw les.error;

    const activityIds = les.value.beweegactiviteiten.flatMap((a) => (a.id ? [a.id] : []));
    const themaIds = les.value.sociaalEmotioneelThemas.flatMap((t) => (t.id ? [t.id] : []));
    const beweegactiviteitIds = activityIds.filter((id) => data.beweegactiviteitIds.includes(id));
    const sociaalEmotioneelThemaIds = themaIds.filter((id) =>
      data.sociaalEmotioneelThemaIds.includes(id),
    );
    if (beweegactiviteitIds.length + sociaalEmotioneelThemaIds.length === 0) {
      return {
        success: false as const,
        formError: m["les.leerling.device_activities_min_one"](),
      };
    }

    const showsEverything =
      beweegactiviteitIds.length === activityIds.length &&
      sociaalEmotioneelThemaIds.length === themaIds.length;
    await setDeviceActivities(
      showsEverything
        ? null
        : { lesId: data.lesson, beweegactiviteitIds, sociaalEmotioneelThemaIds },
    );
    return { success: true as const };
  });
